import { BRAND_OG_IMAGE_URL, BRAND_HANDLE, BRAND_SITE_URL, getBrandDisplayName } from "@/lib/brandConfig";
import { captureSeoMetaAppliedPostHog } from "@/lib/posthogAnalytics";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "./useLanguage";
import { useRegion } from "./useRegion";
import { getTranslation, type Language } from "@/lib/i18n";

const routeToPageKey: Record<string, string> = {
  "/": "index",
  "/content-creator": "contentCreator",
  "/creadora-de-contenido": "creadoraContenido",
  "/influencer": "influencer",
  "/fotos": "fotos",
  "/imagenes": "imagenes",
  "/reels": "reels",
  "/videos": "videos",
  "/video": "video",
  "/lifestyle": "lifestyle",
  "/nutricion": "nutricion",
  "/belleza": "belleza",
  "/viajes": "viajes",
  "/tiktok": "tiktok",
  "/instagram": "instagram",
  "/youtube": "youtube",
  "/erome": "erome",
  "/filtrados": "filtrados",
  "/filtro": "filtro",
  "/leaked": "leaked",
  "/onlyfans": "onlyfans",
  "/pack": "pack",
  "/desnuda": "desnuda",
  "/nude": "nude",
  "/premium": "premium",
  "/privado": "privado",
  "/contenido": "contenido",
  "/exclusivo": "exclusivo",
  "/gratis": "gratis",
  "/galeria": "galeria",
  "/portfolio": "portfolio",
  "/mediakit": "mediakit",
  "/mediakit-intro": "mediakit",
  "/audiovisual": "audiovisual",
  "/fotos-desnuda": "fotosDesnuda",
  "/videos-desnuda": "videosDesnuda",
  "/pornhub": "pornhub",
  "/xvideos": "xvideos",
  "/fotos-gratis": "fotosGratis",
  "/videos-gratis": "videosGratis",
  "/tiktoker": "tiktoker",
  "/modelo": "modelo",
  "/photography": "photography",
  "/artista": "artista",
  "/fashion": "fashion",
  "/wellness": "wellness",
  "/digital-creator": "digitalCreator",
  "/social-media": "socialMedia",
  "/personalidad": "personalidad",
  "/beauty-expert": "beautyExpert",
  "/travel-influencer": "travelInfluencer",
  "/creative": "creative",
  "/argentina": "argentina",
  "/buenos-aires": "buenosAires",
  "/colombia": "colombia",
  "/medellin": "medellin",
  "/bogota": "bogota",
  "/mexico": "mexico",
  "/guadalajara": "guadalajara",
  "/ciudad-de-mexico": "ciudadDeMexico",
  "/chile": "chile",
  "/santiago": "santiago",
  "/espana": "espana",
  "/madrid": "madrid",
  "/barcelona": "barcelona",
  "/deportista": "deportista",
  "/fitness": "fitness",
  "/entrenamiento": "entrenamiento",
  "/atleta": "atleta",
  "/sobre-mi": "sobreMi",
  "/biografia": "biografia",
  "/rutina": "rutina",
  "/recetas": "recetas",
  "/skincare": "skincare",
  "/invitacion-premium": "premiumInvite",
  "/invitacion-premium/success": "premiumInviteSuccess",
  "/premium-invite": "premiumInvite",
  "/invitacion": "premiumInvite",
  "/muchas-gracias": "premiumInviteSuccess",
  "/contacto": "contacto",
  "/latina-influencer": "latinaInfluencer",
  "/hispanic-influencer": "hispanicInfluencer",
  "/latin-influencer": "latinInfluencer",
  "/modella-argentina": "modellaArgentina",
  "/italia": "italia",
  "/london": "london",
  "/usa": "usa",
  "/miami": "miami",
  "/new-york": "newYork",
  "/los-angeles": "losAngeles",
};

/** Path without trailing slash (except root). */
function normalizeSeoPath(pathname: string): string {
  if (pathname === "/") return "/";
  return pathname.replace(/\/+$/, "") || "/";
}

/**
 * Whether this URL should send noindex. Uses segment checks for short tokens
 * (gratis, privado, premium) so we do not false-positive e.g. /fotos-gratis or /grupo-privado.
 */
function computeShouldNoIndex(pathname: string): boolean {
  const p = normalizeSeoPath(pathname);
  const first = p.split("/").filter(Boolean)[0] ?? "";

  if (p.startsWith("/admin") || p.startsWith("/dashboard")) return true;
  // /mediakit is a public brand presentation — indexable
  if (p === "/test-base-template" || p.startsWith("/test-base-template/")) return true;
  if (first.startsWith("test-")) return true;

  const noIndexIfSubstring = [
    "desnuda",
    "nude",
    "pornhub",
    "xvideos",
    "erome",
    "filtrados",
    "filtro",
    "leaked",
    "onlyfans",
    "fotos-desnuda",
    "videos-desnuda",
    "invitacion-premium",
    "muchas-gracias",
    "thank-you-a-lot",
    "premium-invite",
    "test-premium",
  ];
  if (noIndexIfSubstring.some((s) => p.includes(s))) return true;

  const noIndexFirstSegment = new Set([
    "pack",
    "premium",
    "privado",
    "contenido",
    "exclusivo",
    "gratis",
  ]);
  if (noIndexFirstSegment.has(first)) return true;

  return false;
}

interface SEOConfig {
  title: string;
  description: string;
  keywords: string;
  ogImage: string;
  ogTitle?: string;
  ogDescription?: string;
  canonical?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  type?: string;
  locale?: string;
  alternateLocale?: string;
  robots?: string;
  priority?: number;
  changefreq?: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
}

export const useSEO = (pageKeyOrConfig?: string | SEOConfig) => {
  const location = useLocation();
  const { language } = useLanguage();
  const { region } = useRegion();
  const seoTrackKeyRef = useRef<string | null>(null);
  
  useEffect(() => {
    const baseUrl = BRAND_SITE_URL;
    
    // Canonical form: no trailing slash except homepage `/` (matches sitemap + static HTML + Worker).
    // Avoids Ahrefs "Canonical points to redirect" when /path/ is the live URL.
    let cleanPath = location.pathname.split("?")[0].split("#")[0] || "/";
    if (cleanPath.length > 1 && cleanPath.endsWith("/")) {
      cleanPath = cleanPath.slice(0, -1);
    }
    const currentUrl = cleanPath === "/" ? `${baseUrl}/` : `${baseUrl}${cleanPath}`;
    
    let seoData: any;
    
    // Handle both old string format and new config object
    if (typeof pageKeyOrConfig === "object" && pageKeyOrConfig !== null) {
      // New enhanced SEO config
      seoData = pageKeyOrConfig;
    } else {
      // Legacy string-based config
      const key = pageKeyOrConfig || routeToPageKey[location.pathname] || "index";
      seoData = {
        title: getTranslation(language, `pages.${key}.title`),
        description: getTranslation(language, `pages.${key}.description`),
        keywords: getTranslation(language, "common.keywords"),
        ogImage: `${baseUrl}/og.png`,
        ogDescription: getTranslation(language, "common.ogDescription"),
        canonical: currentUrl, // Set canonical to the current clean URL
      };
    }
    
    // Update document title
    document.title = seoData.title;
    document.documentElement.lang = language;
    document.documentElement.setAttribute("data-lang", language);
    
    // Update meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", seoData.description);
    
    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement("meta");
      metaKeywords.setAttribute("name", "keywords");
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute("content", seoData.keywords);
    
    // Update meta robots
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement("meta");
      metaRobots.setAttribute("name", "robots");
      document.head.appendChild(metaRobots);
    }
    // Set appropriate robots directive based on content and page type (see computeShouldNoIndex)
    const shouldNoIndex = computeShouldNoIndex(location.pathname);
    
    const robotsContent = seoData.robots || (
      location.pathname === "/" ? "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" :
      shouldNoIndex ? "noindex, nofollow" :
      "index, follow"
    );
    metaRobots.setAttribute("content", robotsContent);
    
    // Update meta author
    if (seoData.author) {
      let metaAuthor = document.querySelector('meta[name="author"]');
      if (!metaAuthor) {
        metaAuthor = document.createElement("meta");
        metaAuthor.setAttribute("name", "author");
        document.head.appendChild(metaAuthor);
      }
      metaAuthor.setAttribute("content", seoData.author);
    }
    
    // Update canonical URL with proper handling
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    
    // Ensure canonical URL is clean and consistent (no query/hash; no trailing slash except /)
    const canonicalUrl = seoData.canonical || currentUrl;
    let finalCanonicalUrl = canonicalUrl.split("?")[0].split("#")[0];
    try {
      const u = new URL(finalCanonicalUrl, baseUrl);
      const path =
        u.pathname.length > 1 && u.pathname.endsWith("/")
          ? u.pathname.slice(0, -1)
          : u.pathname || "/";
      finalCanonicalUrl = path === "/" ? `${u.origin}/` : `${u.origin}${path}`;
    } catch {
      if (finalCanonicalUrl.length > 1 && finalCanonicalUrl.endsWith("/")) {
        finalCanonicalUrl = finalCanonicalUrl.slice(0, -1);
      }
    }
    canonical.setAttribute("href", finalCanonicalUrl);
    
    // Update Open Graph tags
    const updateOGTag = (property: string, content: string) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    
    updateOGTag("og:title", seoData.ogTitle || seoData.title);
    updateOGTag("og:description", seoData.ogDescription || seoData.description);
    updateOGTag("og:url", finalCanonicalUrl);
    updateOGTag("og:image", seoData.ogImage);
    if (seoData.ogImage?.startsWith("https://")) {
      updateOGTag("og:image:secure_url", seoData.ogImage);
    }
    updateOGTag("og:type", seoData.type || "website");
    updateOGTag(
      "og:locale",
      seoData.locale || language.replace(/-/g, "_"),
    );
    
    if (seoData.alternateLocale) {
      updateOGTag("og:locale:alternate", seoData.alternateLocale);
    }
    
    // Update published/modified time for articles
    if (seoData.publishedTime) {
      updateOGTag("article:published_time", seoData.publishedTime);
    }
    if (seoData.modifiedTime) {
      updateOGTag("article:modified_time", seoData.modifiedTime);
    }
    if (seoData.author) {
      updateOGTag("article:author", seoData.author);
    }
    
    // Update Twitter tags
    const updateTwitterTag = (name: string, content: string) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    
    updateTwitterTag("twitter:card", "summary_large_image");
    updateTwitterTag("twitter:site", `@${BRAND_HANDLE}`);
    updateTwitterTag("twitter:creator", `@${BRAND_HANDLE}`);
    updateTwitterTag("twitter:title", seoData.ogTitle || seoData.title);
    updateTwitterTag("twitter:description", seoData.ogDescription || seoData.description);
    updateTwitterTag("twitter:image", seoData.ogImage);
    
    // Hreflang: never map multiple language codes to the same URL (Ahrefs/Google noise).
    // True bilingual pair has separate paths; everything else gets only x-default.
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    const esPath = `${baseUrl}/creadora-de-contenido`;
    const enPath = `${baseUrl}/content-creator`;
    const addHreflang = (lang: string, url: string) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", lang);
      link.setAttribute("href", url);
      document.head.appendChild(link);
    };
    if (cleanPath === "/creadora-de-contenido" || cleanPath === "/content-creator") {
      addHreflang("es", esPath);
      addHreflang("en", enPath);
      addHreflang("x-default", esPath);
    } else {
      addHreflang("x-default", finalCanonicalUrl);
    }
    
    // Add structured data for bio link (US template)
    if (region === "US") {
      let structuredData = document.querySelector('#structured-data-biolink');
      if (!structuredData) {
        structuredData = document.createElement("script");
        structuredData.setAttribute("type", "application/ld+json");
        structuredData.setAttribute("id", "structured-data-biolink");
        document.head.appendChild(structuredData);
      }
      
      const bioLinkSchema = {
        "@context": "https://schema.org",
        "@type": "Person",
        "name": getBrandDisplayName(),
        "alternateName": BRAND_HANDLE,
        "description": "Argentine model and social content creator — fashion, lifestyle and brand collaborations",
        "url": finalCanonicalUrl,
        "image": BRAND_OG_IMAGE_URL,
        "sameAs": [
          `https://www.instagram.com/${BRAND_HANDLE}/`,
          `https://www.youtube.com/@${BRAND_HANDLE}`
        ],
        "jobTitle": "Model and Content Creator",
        "knowsAbout": ["Fashion", "Lifestyle", "Photography", "Social Media", "Brand Collaborations"],
      };
      
      structuredData.textContent = JSON.stringify(bioLinkSchema);
    }

    const trackKey = `${location.pathname}:${seoData.ogImage}`;
    if (seoTrackKeyRef.current !== trackKey) {
      seoTrackKeyRef.current = trackKey;
      const canonicalOg =
        BRAND_OG_IMAGE_URL || `${baseUrl}/og.png`;
      captureSeoMetaAppliedPostHog({
        og_image_url: seoData.ogImage,
        page_path: location.pathname,
        uses_imagedelivery: seoData.ogImage.includes("imagedelivery.net"),
        uses_canonical_og:
          seoData.ogImage === canonicalOg ||
          seoData.ogImage.endsWith("/og.png"),
      });
    }
    
  }, [location.pathname, language, pageKeyOrConfig, region]);
};
